import { HISTORY_ACTION_MODE, useEditor } from '@candulabs/craft';
import React, { useMemo } from 'react';

import { DismissIconSettings } from 'craft/components/DismissIconSettings';
import { useGenerateInteractionLabelName } from 'craft/containers/Actions/useGenerateInteractionLabelName';
import { Section } from 'craft/containers/Toolbox/components';
import { CssSelectorId, useCss, useMultiNodes } from 'craft/hooks';
import { Action, CATEGORIES, CustomNodeData } from 'craft/types';

interface Props {
  nodeIds: string[];
}

export const CardDismiss = ({ nodeIds }: Props) => {
  const {
    isDismissible,
    actions: { setProps },
  } = useMultiNodes(nodeIds, (node) => ({ isDismissible: node?.data.props.isDismissible }));
  const { actions } = useEditor();
  const generateInteractionLabelName = useGenerateInteractionLabelName(nodeIds);

  const { css: rootCss } = useCss(nodeIds, {
    cssSelectorId: CssSelectorId.Root,
  });

  // little hack to make legacy card dismiss settings work, need to get the variables saved on the card root
  // this value will be overriden by styleguide defaults when saved with the new version
  const defaultStrokeValues = useMemo(() => {
    const baseValues: { stroke?: string; strokeWidth?: string } = Object.keys(rootCss).reduce(
      (acc, key) => {
        if (/^--.*-stroke$/.test(key)) {
          return {
            ...acc,
            stroke: rootCss[key],
          };
        }

        if (/^--.*-stroke-width$/.test(key)) {
          return {
            ...acc,
            strokeWidth: rootCss[key],
          };
        }

        return acc;
      },
      {},
    );

    return {
      strokeWidth: baseValues.strokeWidth,
      stroke: baseValues.stroke,
    };
  }, [rootCss]);

  const onToggleDismissible = () => {
    const newState = !isDismissible;
    setProps({ isDismissible: newState });
    // is dismissable, add an action
    actions.setCustom(
      nodeIds,
      (custom: CustomNodeData) => {
        if (newState) {
          // eslint-disable-next-line no-param-reassign
          custom.actions = [
            ...(custom.actions || []),
            {
              category: CATEGORIES.DISMISS,
              eventName: generateInteractionLabelName('Dismiss'),
            } as Action,
          ];
        } else {
          const newActions = (custom.actions || []).filter(
            (a) => a.category !== CATEGORIES.DISMISS,
          );
          // eslint-disable-next-line no-param-reassign
          custom.actions = newActions.length > 0 ? newActions : undefined;
        }
      },
      HISTORY_ACTION_MODE.MERGE,
    );
  };

  return (
    <Section name="Dismissible Card" isExpanded={isDismissible} onToggle={onToggleDismissible}>
      <DismissIconSettings
        nodeIds={nodeIds}
        dismissCssSelector={CssSelectorId.CardDismiss}
        dismissSVGCssSelector={CssSelectorId.CardDismissSVG}
        defaultValues={{
          [CssSelectorId.Dismiss]: {
            paddingTop: '8px',
            paddingBottom: '8px',
            paddingLeft: '8px',
            paddingRight: '8px',
          },
          [CssSelectorId.DismissSVG]: {
            stroke: defaultStrokeValues.stroke || '#565e61',
            strokeWidth: defaultStrokeValues.strokeWidth || '2px',
          },
        }}
      />
    </Section>
  );
};
